import type { Address, ChainId } from '@morpho-org/blue-sdk'

import {
  InterfaceChainUtils,
  MERKL_API_BASE_URL,
  MERKL_OPPORTUNITIES_URL
} from '@morpho-org/dapp-core'
import { parseJsonResponse, UpstreamResponseError } from '@repo/utils'
import { z } from 'zod'

import { logger } from '@/lib/observability/logger'

const MERKL_API_BASE = `${MERKL_API_BASE_URL}/v4`

export type MerklTarget =
  | {
      /** Borrow side only: Merkl exposes no supply-side market campaign type. */
      kind: 'market'
      chainId: ChainId
      marketId: string
      loanTokenAddress: Address
    }
  | {
      kind: 'vault'
      chainId: ChainId
      identifier: Address
    }

const buildAppUrl = (chainId: ChainId, type: string, identifier: string): string =>
  `${MERKL_OPPORTUNITIES_URL}/${InterfaceChainUtils.getIdentifierById(chainId)}/${type}/${identifier}`

const OpportunityListSchema = z.array(
  z
    .object({
      type: z.string(),
      identifier: z.string()
    })
    .passthrough()
)

const fetchFirstLiveOpportunity = async (
  chainId: ChainId,
  query: Record<string, string>
): Promise<{ type: string; identifier: string } | null> => {
  try {
    const params = new URLSearchParams({
      chainId: chainId.toString(),
      mainProtocolId: 'morpho',
      status: 'LIVE',
      items: '5',
      ...query
    })
    const url = `${MERKL_API_BASE}/opportunities?${params.toString()}`
    const res = await fetch(url)
    if (!res.ok) return null

    const { data: json, error: parseError } = await parseJsonResponse<unknown>(res, { url })
    if (parseError) throw parseError
    const parsed = OpportunityListSchema.safeParse(json)
    return parsed.success ? (parsed.data[0] ?? null) : null
  } catch (error) {
    const loggedError =
      error instanceof Error ? error : new Error('Failed to fetch Merkl opportunity')
    logger.error(loggedError, {
      fingerprint: ['merkl-opportunity-fetch', 'fetch-error'],
      extra: {
        chainId,
        query,
        ...(error instanceof UpstreamResponseError && {
          status: error.status,
          contentType: error.contentType
        })
      }
    })
    return null
  }
}

/**
 * Resolves the canonical Merkl opportunity URL for a Morpho market or vault.
 *
 * Markets: Merkl identifies a market-specific campaign by the first 20 bytes of
 * the market id (`MORPHOBORROW`), and a cross-market loan-token
 * campaign by the loan-token address (`MORPHOBORROW_SINGLETOKEN`). We try the
 * specific one first, then fall back. Only currently-live campaigns are
 * returned (`status=LIVE` server-side) so expired campaigns don't shadow active
 * ones.
 *
 * Vaults: a single listing query against the vault address, scoped to Morpho.
 */
export const fetchMerklOpportunityUrl = async (target: MerklTarget): Promise<string | null> => {
  if (target.kind === 'market') {
    const truncated =
      target.marketId.length >= 42 ? target.marketId.slice(0, 42).toLowerCase() : null
    const tokenAddr = target.loanTokenAddress.toLowerCase()

    if (truncated) {
      const marketSpecific = await fetchFirstLiveOpportunity(target.chainId, {
        identifier: truncated,
        type: 'MORPHOBORROW'
      })
      if (marketSpecific) {
        return buildAppUrl(target.chainId, marketSpecific.type, marketSpecific.identifier)
      }
    }

    const singleToken = await fetchFirstLiveOpportunity(target.chainId, {
      identifier: tokenAddr,
      type: 'MORPHOBORROW_SINGLETOKEN'
    })
    return singleToken
      ? buildAppUrl(target.chainId, singleToken.type, singleToken.identifier)
      : null
  }

  const opp = await fetchFirstLiveOpportunity(target.chainId, {
    identifier: target.identifier
  })
  return opp ? buildAppUrl(target.chainId, opp.type, opp.identifier) : null
}
